import { useContext, useEffect, useCallback } from 'react';

import { ThemeContext } from '@/providers/ThemeProvider';

/**
 * Hook that listens for Cmd+Shift+L (macOS) or Ctrl+Shift+L (Windows/Linux)
 * to toggle the theme between light and dark mode.
 *
 * Note: This shortcut may conflict with browser shortcuts on some systems.
 * We prevent default behavior to minimize conflicts.
 */
export const useThemeKeyboardShortcut = () => {
  const { toggleTheme } = useContext(ThemeContext);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Check for Cmd+Shift+L (macOS) or Ctrl+Shift+L (Windows/Linux)
      // Use event.code for layout-independent key detection
      const isModifierPressed = event.metaKey || event.ctrlKey;
      const isShiftPressed = event.shiftKey;
      const isLKey = event.code === 'KeyL';

      if (isModifierPressed && isShiftPressed && isLKey) {
        event.preventDefault();
        event.stopPropagation();
        toggleTheme();
      }
    },
    [toggleTheme]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
};
